import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Save, Copy, Trash2, FilePlus2, FolderOpen, Download, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import {
  apiGetAnalyticsPivotDataset,
  apiListAnalyticsReports,
  apiGetAnalyticsReport,
  apiCreateAnalyticsReport,
  apiUpdateAnalyticsReport,
  apiDeleteAnalyticsReport,
  getTenantSlug,
} from "@/lib/api";
import { getFieldByKey, type FlatEmployeeRow } from "@/lib/analytics/fieldCatalog";
import { ExportButton } from "@/components/analytics/ExportButton";
import { downloadFile, exportAllTiles, type TileExport } from "@/lib/analytics/exportUtils";
import { PivotConfigPanel } from "./PivotConfigPanel";
import { PivotTileView } from "./PivotOutput";
import { tileExportRows } from "./tileExport";
import { defaultTile, type ReportDoc, type ReportTile } from "./types";

let tileSeq = 0;
function newTileId() {
  tileSeq += 1;
  return `tile-${Date.now()}-${tileSeq}`;
}

export function ReportCanvas() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const draftKey = `besthr_${getTenantSlug()}_pivot_report_draft`;

  const { data: dataset = [], isLoading } = useQuery({
    queryKey: ["analytics-pivot-dataset"],
    queryFn: apiGetAnalyticsPivotDataset,
    staleTime: 5 * 60 * 1000,
  });

  const { data: savedReports = [] } = useQuery({
    queryKey: ["analytics-reports"],
    queryFn: apiListAnalyticsReports,
    staleTime: 60 * 1000,
  });

  // ── Working document (autosaved to localStorage) ──────────────────────────
  const [doc, setDoc] = useState<ReportDoc>(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const parsed = JSON.parse(raw) as ReportDoc;
        if (parsed?.tiles?.length) return parsed;
      }
    } catch {
      /* ignore */
    }
    return { name: "", tiles: [defaultTile(newTileId(), "Report 1")] };
  });
  const [selectedId, setSelectedId] = useState<string | null>(doc.tiles[0]?.id ?? null);
  const [loadedReportId, setLoadedReportId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(draftKey, JSON.stringify(doc));
    } catch {
      /* ignore quota */
    }
  }, [doc, draftKey]);

  const selectedTile = doc.tiles.find((tl) => tl.id === selectedId) ?? null;

  const labelFor = (key: keyof FlatEmployeeRow): string => {
    const f = getFieldByKey(key as string);
    return f ? t(f.labelKey) : String(key);
  };

  // ── Tile ops ──────────────────────────────────────────────────────────────
  const patchTile = (id: string, patch: Partial<ReportTile>) =>
    setDoc((d) => ({ ...d, tiles: d.tiles.map((tl) => (tl.id === id ? { ...tl, ...patch } : tl)) }));

  const addTile = () => {
    const id = newTileId();
    const tile = defaultTile(id, `${t("analytics.pivot.tileUntitled")} ${doc.tiles.length + 1}`);
    setDoc((d) => ({ ...d, tiles: [...d.tiles, tile] }));
    setSelectedId(id);
  };

  const duplicateTile = (id: string) => {
    const src = doc.tiles.find((tl) => tl.id === id);
    if (!src) return;
    const copy: ReportTile = { ...src, id: newTileId(), title: `${src.title} (copy)`, config: structuredClone(src.config) };
    setDoc((d) => {
      const idx = d.tiles.findIndex((tl) => tl.id === id);
      const tiles = [...d.tiles];
      tiles.splice(idx + 1, 0, copy);
      return { ...d, tiles };
    });
    setSelectedId(copy.id);
  };

  const deleteTile = (id: string) => {
    setDoc((d) => {
      const tiles = d.tiles.filter((tl) => tl.id !== id);
      return { ...d, tiles };
    });
    setSelectedId((cur) => (cur === id ? null : cur));
  };

  // ── Report ops ──────────────────────────────────────────────────────────────
  const newReport = () => {
    const id = newTileId();
    setDoc({ name: "", tiles: [defaultTile(id, "Report 1")] });
    setSelectedId(id);
    setLoadedReportId(null);
  };

  const loadReport = async (id: number) => {
    try {
      const r = await apiGetAnalyticsReport(id);
      const cfg = (r.config ?? {}) as ReportDoc;
      const tiles = cfg.tiles?.length ? cfg.tiles : [defaultTile(newTileId(), "Report 1")];
      setDoc({ name: r.name, tiles });
      setSelectedId(tiles[0]?.id ?? null);
      setLoadedReportId(r.id);
    } catch {
      toast.error(t("analytics.pivot.loadError"));
    }
  };

  const saveReport = async (asNew: boolean) => {
    const name = doc.name.trim() || t("analytics.pivot.untitledReport");
    setSaving(true);
    try {
      if (!asNew && loadedReportId != null) {
        await apiUpdateAnalyticsReport(loadedReportId, name, doc);
      } else {
        const created = await apiCreateAnalyticsReport(name, doc);
        setLoadedReportId(created.id);
      }
      setDoc((d) => ({ ...d, name }));
      await queryClient.invalidateQueries({ queryKey: ["analytics-reports"] });
      toast.success(t("analytics.pivot.savedToast"));
    } catch {
      toast.error(t("analytics.pivot.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const deleteReport = async () => {
    if (loadedReportId == null) return;
    if (!window.confirm(t("analytics.pivot.confirmDelete"))) return;
    try {
      await apiDeleteAnalyticsReport(loadedReportId);
      await queryClient.invalidateQueries({ queryKey: ["analytics-reports"] });
      toast.success(t("analytics.pivot.deletedToast"));
      newReport();
    } catch {
      toast.error(t("analytics.pivot.deleteError"));
    }
  };

  // ── Export all ──────────────────────────────────────────────────────────────
  const allTileExports = useMemo<TileExport[]>(
    () =>
      doc.tiles.map((tl) => ({
        title: tl.title,
        rows: tileExportRows(dataset, tl, labelFor, t),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doc.tiles, dataset, t]
  );

  const exportAll = (format: "csv" | "tsv") => {
    const content = exportAllTiles(allTileExports, format);
    const ext = format === "csv" ? "csv" : "xlsx";
    const mime = format === "csv" ? "text/csv;charset=utf-8;" : "application/vnd.ms-excel";
    downloadFile(content, `${(doc.name || "hr-report").replace(/\s+/g, "-").toLowerCase()}.${ext}`, mime);
  };

  return (
    <div className="space-y-4">
      <style>{`@media print { .no-print { display: none !important; } }`}</style>

      {/* Toolbar */}
      <div className="no-print flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-card">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <LayoutGrid className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            value={doc.name}
            onChange={(e) => setDoc((d) => ({ ...d, name: e.target.value }))}
            placeholder={t("analytics.pivot.reportName")}
            className="h-8 max-w-xs"
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <FolderOpen className="h-4 w-4 mr-1.5" />
              {t("analytics.pivot.savedReports")}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>{t("analytics.pivot.savedReports")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {savedReports.length === 0 ? (
              <DropdownMenuItem disabled>{t("analytics.pivot.noSavedReports")}</DropdownMenuItem>
            ) : (
              savedReports.map((r) => (
                <DropdownMenuItem key={r.id} onClick={() => loadReport(r.id)}>
                  {r.name}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="outline" size="sm" onClick={newReport}>
          <FilePlus2 className="h-4 w-4 mr-1.5" />
          {t("analytics.pivot.newReport")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => saveReport(false)} disabled={saving}>
          <Save className="h-4 w-4 mr-1.5" />
          {t("analytics.pivot.save")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => saveReport(true)} disabled={saving}>
          {t("analytics.pivot.saveAs")}
        </Button>
        {loadedReportId != null && (
          <Button variant="ghost" size="sm" onClick={deleteReport} className="text-destructive">
            <Trash2 className="h-4 w-4 mr-1.5" />
            {t("analytics.pivot.delete")}
          </Button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <Button variant="default" size="sm" onClick={addTile}>
            <Plus className="h-4 w-4 mr-1.5" />
            {t("analytics.pivot.addTile")}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-1.5" />
                {t("analytics.pivot.exportAll")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportAll("csv")}>{t("analytics.exportCsv")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportAll("tsv")}>{t("analytics.exportExcel")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.print()}>{t("analytics.exportPdf")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Canvas + config panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Canvas (2/3) */}
        <div className="lg:col-span-2 space-y-4">
          {doc.tiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-20 rounded-lg border border-dashed text-center">
              <p className="text-sm text-muted-foreground">{t("analytics.pivot.emptyCanvas")}</p>
              <Button size="sm" onClick={addTile}>
                <Plus className="h-4 w-4 mr-1.5" />
                {t("analytics.pivot.addTile")}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {doc.tiles.map((tile) => (
                <Card
                  key={tile.id}
                  onClick={() => setSelectedId(tile.id)}
                  className={`cursor-pointer transition-shadow ${selectedId === tile.id ? "ring-2 ring-primary" : "hover:shadow-md"}`}
                >
                  <CardHeader className="flex-row items-center justify-between gap-2 py-3 px-4 border-b space-y-0">
                    <CardTitle className="text-sm truncate">{tile.title}</CardTitle>
                    <div className="flex items-center gap-1 no-print" onClick={(e) => e.stopPropagation()}>
                      <ExportButton
                        data={tileExportRows(dataset, tile, labelFor, t)}
                        filename={(tile.title || "tile").replace(/\s+/g, "-").toLowerCase()}
                      />
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => duplicateTile(tile.id)} title={t("analytics.pivot.duplicate")}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteTile(tile.id)} title={t("analytics.pivot.deleteTile")}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    <PivotTileView dataset={dataset} tile={tile} isLoading={isLoading} />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Config panel (1/3) */}
        <div className="lg:col-span-1 no-print">
          <div className="sticky top-4">
            <Card>
              <CardHeader className="py-3 px-4 border-b">
                <CardTitle className="text-sm">{t("analytics.pivot.configPanelTitle")}</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {selectedTile ? (
                  <PivotConfigPanel tile={selectedTile} onChange={(patch) => patchTile(selectedTile.id, patch)} />
                ) : (
                  <p className="text-sm text-muted-foreground py-8 text-center">{t("analytics.pivot.selectTileHint")}</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
